import { useState } from 'react';
import { Brain, Plus, Lightbulb, CheckCircle2, FolderKanban, Target } from 'lucide-react';

const TRIGGER_PROMPTS = [
  { icon: CheckCircle2, text: "Any commitments made this week that need tracking?" },
  { icon: Brain, text: "Anything on your mind that hasn't been captured?" },
  { icon: FolderKanban, text: "Any new projects starting or ideas brewing?" },
  { icon: Target, text: "Any goals you want to set or revisit?" }
];

export default function MindSweep({ onAddTask, onAddProject, onAddGoal }) {
  const [capturedCount, setCapturedCount] = useState(0);

  const handleCapture = (type) => {
    if (type === 'task') onAddTask();
    else if (type === 'project') onAddProject();
    else if (type === 'goal') onAddGoal();
    setCapturedCount(prev => prev + 1);
  };

  return (
    <div className="flex-1 overflow-auto p-4 md:p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
            <Brain size={32} className="text-accent" />
          </div>
          <h2 className="text-xl font-semibold text-text-primary mb-2">Mind Sweep</h2>
          <p className="text-text-secondary">
            Empty your head. Capture anything that's been on your mind but hasn't made it into your system yet.
          </p>
        </div>

        {/* Trigger prompts */}
        <div className="space-y-3 mb-8">
          {TRIGGER_PROMPTS.map((prompt, idx) => (
            <div
              key={idx}
              className="flex items-center gap-3 p-4 bg-surface-card rounded-xl border border-border"
            >
              <div className="w-10 h-10 rounded-lg bg-surface-hover flex items-center justify-center flex-shrink-0">
                <prompt.icon size={20} className="text-text-secondary" />
              </div>
              <p className="text-text-primary flex-1">{prompt.text}</p>
              <Lightbulb size={16} className="text-text-tertiary" />
            </div>
          ))}
        </div>

        {/* Quick capture buttons */}
        <div className="bg-surface-card rounded-xl border border-border p-6">
          <h3 className="text-sm font-medium text-text-secondary mb-4">Quick Capture</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <button
              onClick={() => handleCapture('task')}
              className="flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-accent/30
                       text-accent hover:bg-accent/5 hover:border-accent transition-all cursor-pointer bg-transparent"
            >
              <Plus size={20} />
              <span className="font-medium">Add Task</span>
            </button>
            <button
              onClick={() => handleCapture('project')}
              className="flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-weekly/30
                       text-weekly hover:bg-weekly/5 hover:border-weekly transition-all cursor-pointer bg-transparent"
            >
              <Plus size={20} />
              <span className="font-medium">Add Project</span>
            </button>
            <button
              onClick={() => handleCapture('goal')}
              className="flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-monthly/30
                       text-monthly hover:bg-monthly/5 hover:border-monthly transition-all cursor-pointer bg-transparent"
            >
              <Plus size={20} />
              <span className="font-medium">Add Goal</span>
            </button>
          </div>
        </div>

        {/* Captured count */}
        {capturedCount > 0 && (
          <div className="mt-4 text-center">
            <span className="text-sm text-success">
              {capturedCount} item{capturedCount !== 1 ? 's' : ''} captured this session
            </span>
          </div>
        )}

        {/* Tips */}
        <div className="mt-8 p-4 bg-surface-hover/50 rounded-xl">
          <p className="text-sm text-text-tertiary">
            <strong className="text-text-secondary">Tip:</strong> Don't worry about organizing yet.
            Just get everything out of your head. You can clarify and organize later.
          </p>
        </div>
      </div>
    </div>
  );
}
